import { hasUserPermission } from '@controlstock/contracts';
import { AlertCircle, LogIn } from 'lucide-react';
import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/app-shell';
import { PageError, PageLoading } from '@/components/page-feedback';
import { useLogout, useSession } from '@/hooks';
import { Brand } from '@/components/brand';
import { Button } from '@/components/ui';
import { userHomePath } from '@/lib/user-home';

const LoginPage = lazy(async () => ({ default: (await import('@/pages/login-page')).LoginPage }));
const DashboardPage = lazy(async () => ({
  default: (await import('@/pages/dashboard-page')).DashboardPage,
}));
const InventoryPage = lazy(async () => ({
  default: (await import('@/pages/inventory-page')).InventoryPage,
}));
const ProductDetailPage = lazy(async () => ({
  default: (await import('@/pages/product-detail-page')).ProductDetailPage,
}));
const ProductCreatePage = lazy(async () => ({
  default: (await import('@/pages/product-create-page')).ProductCreatePage,
}));
const LinkingPage = lazy(async () => ({
  default: (await import('@/pages/linking-page')).LinkingPage,
}));
const OrdersPage = lazy(async () => ({
  default: (await import('@/pages/orders-page')).OrdersPage,
}));
const InvoicesPage = lazy(async () => ({
  default: (await import('@/pages/invoices-page')).InvoicesPage,
}));
const SalesReportsPage = lazy(async () => ({
  default: (await import('@/pages/sales-reports-page')).SalesReportsPage,
}));
const CustomersPage = lazy(async () => ({
  default: (await import('@/pages/business-pages')).CustomersPage,
}));
const CustomerDetailPage = lazy(async () => ({
  default: (await import('@/pages/business-pages')).CustomerDetailPage,
}));
const CollectionsPage = lazy(async () => ({
  default: (await import('@/pages/business-pages')).CollectionsPage,
}));
const CustomerDebtPage = lazy(async () => ({
  default: (await import('@/pages/debts-page')).CustomerDebtPage,
}));
const SellersPage = lazy(async () => ({
  default: (await import('@/pages/business-pages')).SellersPage,
}));
const PurchasesPage = lazy(async () => ({
  default: (await import('@/pages/business-pages')).PurchasesPage,
}));
const PaymentsPage = lazy(async () => ({
  default: (await import('@/pages/business-pages')).PaymentsPage,
}));
const SupplierDebtPage = lazy(async () => ({
  default: (await import('@/pages/debts-page')).SupplierDebtPage,
}));
const ExpensesPage = lazy(async () => ({
  default: (await import('@/pages/business-pages')).ExpensesPage,
}));
const ChecksPage = lazy(async () => ({
  default: (await import('@/pages/checks-page')).ChecksPage,
}));
const BankingPage = lazy(async () => ({
  default: (await import('@/pages/banking-page')).BankingPage,
}));
const SuppliersPage = lazy(async () => ({
  default: (await import('@/pages/business-pages')).SuppliersPage,
}));
const WorkshopProcessesPage = lazy(async () => ({
  default: (await import('@/pages/business-pages')).WorkshopProcessesPage,
}));
const ProductionPage = lazy(async () => ({
  default: (await import('@/pages/production-page')).ProductionPage,
}));
const WorkshopJobsPage = lazy(async () => ({
  default: (await import('@/pages/workshop-jobs-page')).WorkshopJobsPage,
}));
const WorkshopReceiptsPage = lazy(async () => ({
  default: (await import('@/pages/workshop-receipts-page')).WorkshopReceiptsPage,
}));
const RawMaterialsPage = lazy(async () => ({
  default: (await import('@/pages/raw-materials-page')).RawMaterialsPage,
}));
const IntegrationsPage = lazy(async () => ({
  default: (await import('@/pages/integrations-page')).IntegrationsPage,
}));
const ConnectMercadoLibrePage = lazy(async () => ({
  default: (await import('@/pages/connect-mercadolibre-page')).ConnectMercadoLibrePage,
}));
const JobsPage = lazy(async () => ({ default: (await import('@/pages/jobs-page')).JobsPage }));
const UsersPage = lazy(async () => ({ default: (await import('@/pages/users-page')).UsersPage }));
const EmployeesPage = lazy(async () => ({
  default: (await import('@/pages/employees-page')).EmployeesPage,
}));
const SetupPage = lazy(async () => ({ default: (await import('@/pages/setup-page')).SetupPage }));
const ReconciliationPage = lazy(async () => ({
  default: (await import('@/pages/reconciliation-page')).ReconciliationPage,
}));
const NotFoundPage = lazy(async () => ({
  default: (await import('@/pages/not-found-page')).NotFoundPage,
}));

type ErrorBoundaryState = Readonly<{ hasError: boolean }>;

class AppErrorBoundary extends Component<Readonly<{ children: ReactNode }>, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Never render an exception payload. Browser console is useful in development only.
    if (import.meta.env.DEV) console.error('Unhandled UI error', error, errorInfo);
  }

  public override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <main className="grid min-h-screen place-items-center bg-slate-25 p-5">
          <div className="max-w-md rounded-2xl border border-rose-100 bg-white p-8 text-center shadow-sm">
            <span className="mx-auto grid size-11 place-items-center rounded-xl bg-rose-50 text-rose-600">
              <AlertCircle size={22} aria-hidden="true" />
            </span>
            <h1 className="mt-4 text-lg font-semibold text-ink-950">
              La aplicación encontró un problema
            </h1>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Podés volver a cargar la página. Si el problema persiste, conservá el identificador de
              la solicitud al contactar soporte.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <LogIn size={16} aria-hidden="true" />
              Recargar aplicación
            </button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

function ProtectedLayout(): React.JSX.Element {
  const session = useSession();
  const logout = useLogout();
  const location = useLocation();
  const navigate = useNavigate();

  if (session.isPending) return <PageLoading label="Verificando tu sesión segura…" />;
  if (session.isError) {
    return (
      <PageError
        title="No podemos verificar tu sesión"
        description="La aplicación no pudo comunicarse con el servidor. Intentá nuevamente."
        onRetry={() => void session.refetch()}
      />
    );
  }
  if (!session.data) return <Navigate to="/login" replace state={{ from: location }} />;

  function logoutUser(): void {
    void logout
      .mutateAsync()
      .then(() => {
        void navigate('/login', { replace: true });
      })
      .catch(() => {
        // The session may remain valid when the server cannot be reached; retain the current screen.
      });
  }

  if (session.data.user.role === 'ML_INTEGRATOR') {
    if (location.pathname !== '/conectar-mercadolibre') {
      return <Navigate to="/conectar-mercadolibre" replace />;
    }
    return (
      <div className="min-h-screen bg-slate-25">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4">
          <Brand />
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{session.data.user.displayName}</span>
            <Button variant="outline" onClick={logoutUser} loading={logout.isPending}>
              Cerrar sesión
            </Button>
          </div>
        </header>
        {logout.isError && (
          <p role="alert" className="p-4 text-rose-600">
            No pudimos cerrar la sesión. Intentá nuevamente.
          </p>
        )}
        <main className="mx-auto max-w-2xl px-5 py-10">
          <Outlet />
        </main>
      </div>
    );
  }

  if (
    (location.pathname === '/inventario' || location.pathname.startsWith('/productos/')) &&
    !hasUserPermission(session.data.user, 'inventory.read')
  )
    return <Navigate to={userHomePath(session.data.user)} replace />;

  return (
    <AppShell session={session.data} onLogout={logoutUser}>
      <Outlet />
    </AppShell>
  );
}

function AdminOnly(): React.JSX.Element {
  const session = useSession();
  if (session.isPending) return <PageLoading label="Verificando permisos…" />;
  if (!session.data || session.data.user.role !== 'ADMIN')
    return <Navigate to="/inventario" replace />;
  return <Outlet />;
}

function ProductCreateOnly(): React.JSX.Element {
  const session = useSession();
  if (session.isPending) return <PageLoading label="Verificando permisos…" />;
  if (!session.data || !hasUserPermission(session.data.user, 'inventory.product.create'))
    return <Navigate to="/inventario" replace />;
  return <Outlet />;
}

function SalesOnly(): React.JSX.Element {
  const session = useSession();
  if (session.isPending) return <PageLoading label="Verificando permisos…" />;
  const allowed = hasUserPermission(session.data?.user, 'sales.read');
  if (!allowed) return <Navigate to="/inventario" replace />;
  return <Outlet />;
}

function SalesReportsOnly(): React.JSX.Element {
  const session = useSession();
  if (session.isPending) return <PageLoading label="Verificando permisos…" />;
  if (!hasUserPermission(session.data?.user, 'reports.sales.read'))
    return <Navigate to="/inventario" replace />;
  return <Outlet />;
}

function DashboardOnly(): React.JSX.Element {
  const session = useSession();
  if (session.isPending) return <PageLoading label="Verificando permisos…" />;
  if (!hasUserPermission(session.data?.user, 'dashboard.read'))
    return <Navigate to="/inventario" replace />;
  return <Outlet />;
}

function HomeRedirect(): React.JSX.Element {
  const session = useSession();
  if (session.isPending) return <PageLoading label="Cargando tu espacio de trabajo…" />;
  return <Navigate to={userHomePath(session.data?.user)} replace />;
}

export function App(): React.JSX.Element {
  return (
    <AppErrorBoundary>
      <Suspense fallback={<PageLoading label="Cargando pantalla…" />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<HomeRedirect />} />
            <Route
              path="/sin-acceso"
              element={
                <p className="p-6">
                  No tenés módulos habilitados. Pedile al administrador que revise tus permisos.
                </p>
              }
            />
            <Route path="/conectar-mercadolibre" element={<ConnectMercadoLibrePage />} />
            <Route path="/inventario" element={<InventoryPage />} />
            <Route path="/productos/:productId" element={<ProductDetailPage />} />
            <Route path="/movimientos" element={<Navigate to="/inventario" replace />} />
            <Route element={<SalesOnly />}>
              <Route path="/ventas/pedidos" element={<OrdersPage />} />
              <Route path="/ventas/facturacion" element={<InvoicesPage />} />
              <Route path="/ventas/clientes" element={<CustomersPage />} />
              <Route path="/ventas/clientes/:id" element={<CustomerDetailPage />} />
              <Route path="/ventas/cobros" element={<CollectionsPage />} />
              <Route path="/ventas/deuda-clientes" element={<CustomerDebtPage />} />
              <Route path="/ventas/vendedores" element={<SellersPage />} />
              <Route path="/ordenes" element={<Navigate to="/ventas/pedidos" replace />} />
            </Route>
            <Route element={<SalesReportsOnly />}>
              <Route path="/reportes/ventas" element={<SalesReportsPage />} />
            </Route>
            <Route element={<DashboardOnly />}>
              <Route path="/dashboard" element={<DashboardPage />} />
            </Route>
            <Route element={<ProductCreateOnly />}>
              <Route path="/productos/nuevo" element={<ProductCreatePage />} />
            </Route>
            <Route element={<AdminOnly />}>
              <Route
                path="/vinculacion"
                element={<Navigate to="/configuracion/integraciones/vinculacion" replace />}
              />
              <Route
                path="/integraciones"
                element={<Navigate to="/configuracion/integraciones" replace />}
              />
              <Route
                path="/jobs"
                element={<Navigate to="/configuracion/integraciones/jobs" replace />}
              />
              <Route path="/configuracion/integraciones" element={<IntegrationsPage />} />
              <Route path="/configuracion/integraciones/vinculacion" element={<LinkingPage />} />
              <Route path="/configuracion/integraciones/jobs" element={<JobsPage />} />
              <Route path="/compras" element={<Navigate to="/compras/facturacion" replace />} />
              <Route path="/compras/facturacion" element={<PurchasesPage />} />
              <Route path="/compras/pagos" element={<PaymentsPage />} />
              <Route path="/compras/deuda-proveedores" element={<SupplierDebtPage />} />
              <Route path="/compras/gastos" element={<ExpensesPage />} />
              <Route path="/compras/proveedores" element={<SuppliersPage />} />
              <Route path="/compras/materias-primas" element={<RawMaterialsPage />} />
              <Route path="/tesoreria/bancos" element={<BankingPage />} />
              <Route path="/tesoreria/cheques" element={<ChecksPage />} />
              <Route path="/cheques" element={<Navigate to="/tesoreria/cheques" replace />} />
              <Route path="/produccion" element={<ProductionPage />} />
              <Route path="/produccion/talleres" element={<WorkshopJobsPage />} />
              <Route path="/produccion/recepciones" element={<WorkshopReceiptsPage />} />
              <Route path="/pagos" element={<Navigate to="/compras/pagos" replace />} />
              <Route path="/gastos" element={<Navigate to="/compras/gastos" replace />} />
              <Route path="/proveedores" element={<Navigate to="/compras/proveedores" replace />} />
              <Route path="/usuarios" element={<Navigate to="/configuracion/usuarios" replace />} />
              <Route
                path="/empleados"
                element={<Navigate to="/configuracion/empleados" replace />}
              />
              <Route path="/configuracion/usuarios" element={<UsersPage />} />
              <Route path="/configuracion/empleados" element={<EmployeesPage />} />
              <Route path="/configuracion/procesos-talleres" element={<WorkshopProcessesPage />} />
              <Route path="/configuracion" element={<SetupPage />} />
              <Route path="/reconciliacion" element={<ReconciliationPage />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </AppErrorBoundary>
  );
}
